import React, { forwardRef } from 'react';
import {
  PALETTE, FONT_SERIF, FONT_SANS, FONT_MONO,
  HaleMark, ToxIntelMark, Backplate, Card, Eyebrow, Headline, Body, Pill, Bullet, Rule,
} from './primitives';

/* Asset 4 — Brand Dashboard Mockup
 *
 * Mirrors the actual /hale/dashboard surface: status badge, assigned
 * reviewer card, outstanding items list, documentation strip, badge
 * progress, and a single "next action" CTA. Uses the same component
 * vocabulary as HaleDashboardPage.jsx so it reads as the real product. */

const VB_W = 1600;
const VB_H = 900;

const BrandDashboardMockupAsset = forwardRef(function BrandDashboardMockupAsset(_props, ref) {
  return (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width="100%"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Hale brand dashboard mockup"
    >
      <Backplate w={VB_W} h={VB_H} />

      {/* Header strip */}
      <ToxIntelMark x={48} y={56} fontSize={24} />
      <g transform="translate(140,32)">
        <rect width={92} height={26} fill={PALETTE.SLATE} rx={2} />
        <text x={46} y={17} textAnchor="middle" fill={PALETTE.IVORY} fontFamily={FONT_MONO} fontSize={10} letterSpacing={2}>
          · HALE
        </text>
      </g>
      <text x={VB_W - 48} y={56} textAnchor="end" fill={PALETTE.MUTED} fontFamily={FONT_MONO} fontSize={11} letterSpacing={2}>
        BRAND DASHBOARD · LIVE PRODUCT VIEW
      </text>
      <Rule x1={0} x2={VB_W} y={86} />

      {/* Hero title block */}
      <HaleMark x={48} y={132} w={84} h={36} fontSize={24} />
      <Eyebrow x={48} y={196} text="Hale brand dashboard · sample" />
      <Headline x={48} y={246} text="Sample Brand · pilot status" size={42} />
      <text x={48} y={282} fill={PALETTE.MUTED} fontFamily={FONT_SANS} fontSize={16}>
        Phase 1 pilot · Reviewed™ pathway · onboarded {`'`}26 Feb
      </text>

      {/* Status row — 4 KPI cards */}
      {[
        { k: 'Application status', v: 'Approved',          tone: PALETTE.GREEN },
        { k: 'Pathway',            v: 'Reviewed™',         tone: PALETTE.CORAL },
        { k: 'Assigned reviewer',  v: 'Dr. K. Acquah',     tone: PALETTE.SLATE },
        { k: 'Badge progress',     v: '4 of 5 steps',      tone: PALETTE.GOLD },
      ].map((kpi, i) => (
        <g key={kpi.k} transform={`translate(${48 + i * 376},340)`}>
          <Card x={0} y={0} w={350} h={130}>
            <g transform="translate(24,24)">
              <text x={0} y={0} fill={PALETTE.MUTED} fontFamily={FONT_MONO} fontSize={11} letterSpacing={2} dominantBaseline="hanging">
                {kpi.k.toUpperCase()}
              </text>
              <text x={0} y={60} fill={PALETTE.SLATE} fontFamily={FONT_SERIF} fontWeight={700} fontSize={32}>
                {kpi.v}
              </text>
              <rect x={0} y={82} width={50} height={3} fill={kpi.tone} />
            </g>
          </Card>
        </g>
      ))}

      {/* Outstanding items + Documentation strip */}
      <g transform="translate(48,500)">
        <Card x={0} y={0} w={920} h={340}>
          <g transform="translate(28,28)">
            <Eyebrow x={0} y={0} text="Outstanding items · awaiting brand action" />
            <text x={0} y={42} fill={PALETTE.SLATE} fontFamily={FONT_SERIF} fontWeight={700} fontSize={26}>
              3 documents pending
            </text>
            <g transform="translate(0,76)">
              {[
                'Submit revised INCI list for SKU #2204',
                'Upload supplier safety data (fragrance compound)',
                'Acknowledge reviewer note on packaging claim',
              ].map((t, i) => (
                <g key={t} transform={`translate(0,${i * 56})`}>
                  <rect x={0} y={0} width={860} height={48} fill="rgba(11,20,38,0.025)" rx={2} />
                  <Bullet x={16} y={18} text={t} size={15} />
                  <text x={840} y={29} textAnchor="end" fill={PALETTE.CORAL} fontFamily={FONT_MONO} fontSize={11} letterSpacing={2}>
                    OPEN →
                  </text>
                </g>
              ))}
            </g>
          </g>
        </Card>
      </g>

      {/* Right column: documentation + next action */}
      <g transform="translate(992,500)">
        <Card x={0} y={0} w={560} h={160}>
          <g transform="translate(28,24)">
            <Eyebrow x={0} y={0} text="Documentation submitted · 12 of 15" />
            <text x={0} y={48} fill={PALETTE.SLATE} fontFamily={FONT_SERIF} fontWeight={700} fontSize={22}>
              INCI · Suppliers · Packaging
            </text>
            <rect x={0} y={70} width={500} height={8} fill="rgba(11,20,38,0.08)" rx={4} />
            <rect x={0} y={70} width={400} height={8} fill={PALETTE.CORAL} rx={4} />
            <text x={0} y={104} fill={PALETTE.MUTED} fontFamily={FONT_SANS} fontSize={13}>
              80% complete · 3 items remaining before reviewer sign-off.
            </text>
          </g>
        </Card>

        <Card x={0} y={180} w={560} h={160} fill="rgba(255,78,106,0.08)" stroke={PALETTE.CORAL}>
          <g transform="translate(28,24)">
            <Eyebrow x={0} y={0} text="Next action · 1 step to badge" />
            <text x={0} y={48} fill={PALETTE.SLATE} fontFamily={FONT_SERIF} fontWeight={700} fontSize={22}>
              Submit final supplier documentation
            </text>
            <text x={0} y={76} fill={PALETTE.MUTED} fontFamily={FONT_SANS} fontSize={13}>
              Once received, the reviewer queue moves Sample Brand to badge issuance.
            </text>
            <g transform="translate(0,98)">
              <rect width={220} height={36} fill={PALETTE.CORAL} rx={2} />
              <text x={110} y={23} textAnchor="middle" fill={PALETTE.SLATE} fontFamily={FONT_MONO} fontSize={11} letterSpacing={2} fontWeight={600}>
                OPEN UPLOAD WORKFLOW →
              </text>
            </g>
          </g>
        </Card>
      </g>

      <text x={48} y={VB_H - 30} fill={PALETTE.MUTED} fontFamily={FONT_MONO} fontSize={11} letterSpacing={2}>
        HALE × TOXINTEL · BRAND DASHBOARD · LIVE COMPONENT MOCKUP
      </text>
    </svg>
  );
});

export default BrandDashboardMockupAsset;
